import React, { useState, useEffect, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { 
  Clock, Users, Star, MapPin,
  Calendar, Crown, Lock, Heart, ArrowRight,
  Waves, Sparkles, Zap
} from "lucide-react";
import { getSiteProfile } from "../lib/site-profile";
import { CARIBBEAN_ISLANDS } from "../lib/caribbeanIslands";
import { isProUser } from "../lib/patron";

const ExperiencesPage = () => {
  const { user } = useAuth();
  const siteProfile = getSiteProfile();
  const [searchParams, setSearchParams] = useSearchParams();
  const [experiences, setExperiences] = useState([]);
  const [loading, setLoading] = useState(true);

  const country = searchParams.get("country") || "barbados";
  const category = searchParams.get("category") || "";

  const countries = CARIBBEAN_ISLANDS;

  const categories = [
    { label: "All Experiences", value: "" },
    { label: "Water Sports", value: "adventure" },
    { label: "Tours & Sightseeing", value: "attraction" },
    { label: "Food & Dining", value: "restaurant" },
    { label: "Nightlife", value: "nightclub" },
    { label: "Wellness", value: "wellness" },
  ];

  const fetchExperiences = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 50 };
      if (country) params.country = country;
      if (category) params.category = category;

      const response = await axios.get(`${API}/experiences`, { params, withCredentials: true });
      setExperiences(response.data.experiences || []);
    } catch (error) {
      console.error("Fetch experiences error:", error);
    } finally {
      setLoading(false);
    }
  }, [country, category]);

  useEffect(() => {
    fetchExperiences();
  }, [fetchExperiences]);

  const updateFilter = (key, value) => {
    const params = new URLSearchParams(searchParams);
    if (value) {
      params.set(key, value);
    } else {
      params.delete(key);
    }
    setSearchParams(params);
  };

  const isPro = isProUser(user);

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 via-slate-800 to-cyan-900">
      {/* Header */}
      <header className="sticky top-[var(--mansion-ticker-height)] z-40 bg-slate-900/80 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-500 to-orange-500 flex items-center justify-center">
                <Waves className="w-5 h-5 text-white" />
              </div>
              <span className="font-serif font-bold text-lg text-white hidden sm:block">
                {siteProfile.brandName}
              </span>
            </Link>

            <div className="flex items-center gap-4">
              <nav className="hidden md:flex items-center gap-1">
                <Link to="/discover" className="px-4 py-2 rounded-full text-white/70 hover:text-white hover:bg-white/10 transition-colors">
                  Discover
                </Link>
                <Link to="/experiences" className="px-4 py-2 rounded-full bg-white/10 text-white font-medium">
                  Experiences
                </Link>
                <Link to="/stays" className="px-4 py-2 rounded-full text-white/70 hover:text-white hover:bg-white/10 transition-colors">
                  Stays
                </Link>
              </nav>
              {user ? (
                <Link to="/dashboard" className="px-5 py-2.5 rounded-full bg-gradient-to-r from-orange-500 to-amber-500 text-white font-medium hover:shadow-lg transition-all">
                  Dashboard
                </Link>
              ) : (
                <Link to="/auth" className="px-5 py-2.5 rounded-full bg-gradient-to-r from-orange-500 to-amber-500 text-white font-medium hover:shadow-lg transition-all">
                  Get Started
                </Link>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative py-20 px-4 md:px-8">
        <div className="absolute inset-0 bg-gradient-to-br from-orange-500/20 via-transparent to-cyan-500/20" />
        <div className="max-w-7xl mx-auto relative z-10">
          {/* Country Selector */}
          <div className="flex flex-wrap gap-2 mb-8">
            {countries.map(c => (
              <button
                key={c.code}
                onClick={() => updateFilter("country", c.code)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                  country === c.code 
                    ? "bg-white text-gray-900 shadow-lg" 
                    : "bg-white/10 text-white hover:bg-white/20"
                }`}
              >
                <span className="mr-1.5">{c.flag}</span>
                {c.name}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 rounded-xl bg-gradient-to-br from-orange-500 to-amber-500">
              <Calendar className="w-5 h-5 text-white" />
            </div>
            <span className="text-sm font-semibold text-orange-400 uppercase tracking-wider">Book Today</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-serif font-bold text-white mb-4">
            Unforgettable Experiences
          </h1>
          <p className="text-xl text-white/70 max-w-2xl">
            From sunrise catamaran cruises to midnight beach parties. Book curated experiences that make your trip legendary.
          </p>
        </div>
      </section>

      {/* Filters */}
      <section className="px-4 md:px-8 pb-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-wrap gap-3">
            {categories.map(cat => (
              <button
                key={cat.value}
                onClick={() => updateFilter("category", cat.value)}
                className={`px-5 py-2.5 rounded-full font-medium transition-all ${
                  category === cat.value 
                    ? "bg-gradient-to-r from-orange-500 to-amber-500 text-white shadow-lg" 
                    : "bg-white/10 text-white hover:bg-white/20"
                }`}
              >
                {cat.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Experiences Grid */}
      <section className="px-4 md:px-8 pb-20">
        <div className="max-w-7xl mx-auto">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="rounded-2xl bg-white/10 animate-pulse h-96" />
              ))}
            </div>
          ) : experiences.length === 0 ? (
            <div className="text-center py-20">
              <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-white/10 flex items-center justify-center">
                <Calendar className="w-10 h-10 text-white/50" />
              </div>
              <h3 className="text-xl font-semibold text-white mb-2">No experiences found</h3>
              <p className="text-white/60 mb-6">Try selecting a different category or country</p>
              <Button onClick={() => updateFilter("category", "")} className="rounded-full">
                View All Experiences
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {experiences.map((exp, idx) => (
                <ExperienceCard key={exp.experience_id} experience={exp} isPro={isPro} index={idx} />
              ))}
            </div>
          )}

          {/* PRO Banner */}
          {!isPro && experiences.length > 0 && (
            <div className="mt-16 p-8 md:p-12 rounded-3xl bg-gradient-to-r from-amber-500/20 to-orange-500/20 border border-amber-500/30 text-center">
              <Crown className="w-12 h-12 mx-auto mb-4 text-amber-400" />
              <h3 className="text-2xl md:text-3xl font-serif font-bold text-white mb-3">Book Instantly with PRO</h3>
              <p className="text-white/70 mb-6 max-w-lg mx-auto">
                PRO members get instant booking confirmation, 10% discounts on all experiences, and exclusive access to limited spots.
              </p>
              <Link to="/premium">
                <Button className="px-8 py-6 rounded-full bg-gradient-to-r from-amber-500 to-orange-500 text-white font-semibold hover:shadow-xl transition-all">
                  Upgrade to PRO - $9.99/mo
                </Button>
              </Link>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

const EXPERIENCE_FALLBACKS = [
  'https://images.unsplash.com/photo-1544551763-46a013bb70d5?w=800&q=80',
  'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=800&q=80',
  'https://images.unsplash.com/photo-1476514525535-07fb3b4ae5f1?w=800&q=80',
  'https://images.unsplash.com/photo-1519046904884-53103b34b206?w=800&q=80',
  'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=800&q=80',
  'https://images.unsplash.com/photo-1505881502353-a1986add3762?w=800&q=80',
];

// Experience Card Component
function ExperienceCard({ experience, isPro, index }) {
  const canBookDirectly = experience.can_book_directly;
  const imageUrl = experience.image_url || EXPERIENCE_FALLBACKS[index % EXPERIENCE_FALLBACKS.length];

  return (
    <Link 
      to={`/experience/${experience.experience_id}`}
      className="group relative flex flex-col rounded-2xl overflow-hidden bg-white/5 backdrop-blur-sm border border-white/10 hover:border-white/30 hover:bg-white/10 transition-all duration-500 hover:-translate-y-1"
    >
      {/* Image */}
      <div className="relative h-56 overflow-hidden">
        <img 
          src={imageUrl}
          alt={experience.name}
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
        
        {/* Badges */}
        <div className="absolute top-4 left-4 flex gap-2">
          {experience.is_bestseller && (
            <Badge className="bg-gradient-to-r from-orange-500 to-amber-500 text-white border-0">
              <Zap className="w-3 h-3 mr-1" /> Bestseller
            </Badge>
          )}
          {experience.limited_availability && (
            <Badge className="bg-rose-500 text-white border-0">Limited Spots</Badge>
          )}
        </div>

        {/* Favorite */}
        <button className="absolute top-4 right-4 w-9 h-9 rounded-full bg-black/30 backdrop-blur-sm flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-black/50">
          <Heart className="w-5 h-5 text-white" />
        </button>

        {/* Price */}
        <div className="absolute bottom-4 left-4">
          <div className="px-4 py-2 rounded-full bg-white/20 backdrop-blur-sm text-white">
            <span className="text-sm">From</span>
            <span className="text-xl font-bold ml-1">${experience.price}</span>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 p-5">
        <div className="flex items-center gap-2 text-white/60 text-sm mb-2">
          <MapPin className="w-4 h-4" />
          <span>{experience.location || experience.country}</span>
          {experience.rating && (
            <>
              <span className="mx-1">•</span>
              <Star className="w-4 h-4 text-amber-400 fill-amber-400" />
              <span>{experience.rating}</span>
            </>
          )}
        </div>
        <h3 className="font-serif font-bold text-white text-lg mb-3 line-clamp-2 group-hover:text-orange-300 transition-colors">
          {experience.name}
        </h3>
        <p className="text-white/60 text-sm line-clamp-2 mb-4">{experience.short_description || experience.description}</p>
        
        <div className="flex items-center gap-4 text-white/60 text-sm">
          <div className="flex items-center gap-1">
            <Clock className="w-4 h-4" />
            <span>{experience.duration || "3 hours"}</span>
          </div>
          <div className="flex items-center gap-1">
            <Users className="w-4 h-4" />
            <span>Up to {experience.max_participants || 10}</span>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="p-5 pt-0">
        {canBookDirectly ? (
          <div className="flex items-center justify-between">
            <span className="text-green-400 text-sm font-medium flex items-center gap-1">
              <Sparkles className="w-4 h-4" /> Instant Confirmation
            </span>
            <ArrowRight className="w-5 h-5 text-white/50 group-hover:text-orange-400 group-hover:translate-x-1 transition-all" />
          </div>
        ) : (
          <div className="flex items-center justify-between">
            <span className="text-white/50 text-sm flex items-center gap-1">
              <Lock className="w-4 h-4" /> Inquiry Only
            </span>
            <div className="text-xs text-amber-400">PRO for instant booking</div>
          </div>
        )}
      </div>
    </Link>
  );
}

export default ExperiencesPage;
